import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Undo2, ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { confidenceBand, formatNumber, patchExtractedData } from "@/lib/documents";

function numOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const parsed = Number(String(v).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function asString(v) {
  return v === null || v === undefined ? "" : String(v);
}

function FieldRow({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">
        {label}
      </Label>
      <Input
        className="mt-1 h-9"
        type={type}
        value={asString(value)}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </div>
  );
}

/**
 * ExtractionReview — editable view of a document's extracted_data.
 * Props:
 *   document     : { id, filename, extracted_data, quality_score, ... }
 *   onPatched(fresh) : callback after successful patch
 */
export default function ExtractionReview({ document: doc, onPatched }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(doc.extracted_data || {})));
  const [saving, setSaving] = useState(false);
  const [openAdvanced, setOpenAdvanced] = useState(false);
  const band = confidenceBand(doc.quality_score);

  const budget_lines = draft.budget_lines || [];
  const loans = draft.loans || [];
  const legal_articles = draft.legal_articles || [];

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(doc.extracted_data || {}),
    [draft, doc.extracted_data]
  );

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const setTaxRate = (key, value) =>
    setDraft((d) => ({
      ...d,
      tax_rates: { ...(d.tax_rates || {}), [key]: value },
    }));

  const updateBudgetLine = (i, field, value) => {
    setDraft((d) => {
      const arr = [...(d.budget_lines || [])];
      arr[i] = { ...arr[i], [field]: value };
      return { ...d, budget_lines: arr };
    });
  };
  const addBudgetLine = () =>
    setDraft((d) => ({
      ...d,
      budget_lines: [
        ...(d.budget_lines || []),
        { sector: "", amount: 0, percentage: null },
      ],
    }));
  const removeBudgetLine = (i) =>
    setDraft((d) => {
      const arr = [...(d.budget_lines || [])];
      arr.splice(i, 1);
      return { ...d, budget_lines: arr };
    });

  const updateLoan = (i, field, value) => {
    setDraft((d) => {
      const arr = [...(d.loans || [])];
      arr[i] = { ...arr[i], [field]: value };
      return { ...d, loans: arr };
    });
  };
  const addLoan = () =>
    setDraft((d) => ({
      ...d,
      loans: [
        ...(d.loans || []),
        { creditor: "", amount: 0, rate: null, maturity: null },
      ],
    }));
  const removeLoan = (i) =>
    setDraft((d) => {
      const arr = [...(d.loans || [])];
      arr.splice(i, 1);
      return { ...d, loans: arr };
    });

  const handleSave = async () => {
    const normalized = {
      ...draft,
      total_revenue: numOrNull(draft.total_revenue),
      total_expenditure: numOrNull(draft.total_expenditure),
      fiscal_deficit: numOrNull(draft.fiscal_deficit),
      public_debt: numOrNull(draft.public_debt),
      tax_rates: {
        ir: numOrNull(draft.tax_rates?.ir),
        vat: numOrNull(draft.tax_rates?.vat),
        social: numOrNull(draft.tax_rates?.social),
      },
      budget_lines: (draft.budget_lines || []).map((b) => ({
        sector: asString(b.sector).trim(),
        amount: numOrNull(b.amount) ?? 0,
        percentage: numOrNull(b.percentage),
      })),
      loans: (draft.loans || []).map((l) => ({
        creditor: asString(l.creditor).trim(),
        amount: numOrNull(l.amount) ?? 0,
        rate: numOrNull(l.rate),
        maturity: numOrNull(l.maturity),
      })),
    };
    setSaving(true);
    try {
      const fresh = await patchExtractedData(doc.id, normalized);
      toast.success("Corrections enregistrées.");
      onPatched?.(fresh);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || e.message || "Impossible d'enregistrer."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft(JSON.parse(JSON.stringify(doc.extracted_data || {})));
  };

  return (
    <Card
      className={cn(
        "overflow-hidden border-l-4",
        band.color === "green" && "border-l-green-500",
        band.color === "orange" && "border-l-amber-500",
        band.color === "red" && "border-l-destructive",
        band.color === "muted" && "border-l-border"
      )}
      data-testid={`extraction-review-${doc.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="truncate text-base">
              {doc.filename}
            </CardTitle>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span>{doc.doc_type}</span>
              <span>·</span>
              <span>{doc.page_count || "—"} pages</span>
              {doc.extracted_data?.fiscal_year ? (
                <>
                  <span>·</span>
                  <span>Exercice {doc.extracted_data.fiscal_year}</span>
                </>
              ) : null}
            </div>
          </div>
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold",
              band.color === "green" &&
                "bg-green-500/10 text-green-700 dark:text-green-400 border border-green-500/30",
              band.color === "orange" &&
                "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30",
              band.color === "red" &&
                "bg-destructive/10 text-destructive border border-destructive/30"
            )}
            data-testid={`confidence-badge-${doc.id}`}
          >
            <span className="h-2 w-2 rounded-full bg-current" />
            {band.label} · {band.pct}%
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Key fields */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <FieldRow
            label="Pays"
            value={draft.country}
            onChange={(v) => setField("country", v)}
            testId={`review-country-${doc.id}`}
          />
          <FieldRow
            label="Exercice fiscal"
            value={draft.fiscal_year}
            onChange={(v) => setField("fiscal_year", v)}
            testId={`review-year-${doc.id}`}
          />
          <FieldRow
            label="Devise"
            value={draft.currency}
            onChange={(v) => setField("currency", v)}
            testId={`review-currency-${doc.id}`}
          />
          <FieldRow
            label="Type"
            value={draft.doc_type}
            onChange={(v) => setField("doc_type", v)}
            testId={`review-doctype-${doc.id}`}
          />
        </div>

        {/* Macro numbers */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <FieldRow
            label="Recettes totales"
            value={draft.total_revenue}
            onChange={(v) => setField("total_revenue", v)}
            testId={`review-revenue-${doc.id}`}
          />
          <FieldRow
            label="Dépenses totales"
            value={draft.total_expenditure}
            onChange={(v) => setField("total_expenditure", v)}
            testId={`review-expenditure-${doc.id}`}
          />
          <FieldRow
            label="Déficit budgétaire"
            value={draft.fiscal_deficit}
            onChange={(v) => setField("fiscal_deficit", v)}
            testId={`review-deficit-${doc.id}`}
          />
          <FieldRow
            label="Dette publique"
            value={draft.public_debt}
            onChange={(v) => setField("public_debt", v)}
            testId={`review-debt-${doc.id}`}
          />
        </div>

        {/* Tax rates */}
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Taux d'imposition (%)
          </div>
          <div className="grid grid-cols-3 gap-3">
            <FieldRow
              label="IR"
              value={draft.tax_rates?.ir}
              onChange={(v) => setTaxRate("ir", v)}
              testId={`review-tax-ir-${doc.id}`}
            />
            <FieldRow
              label="TVA"
              value={draft.tax_rates?.vat}
              onChange={(v) => setTaxRate("vat", v)}
              testId={`review-tax-vat-${doc.id}`}
            />
            <FieldRow
              label="Cotisations sociales"
              value={draft.tax_rates?.social}
              onChange={(v) => setTaxRate("social", v)}
              testId={`review-tax-social-${doc.id}`}
            />
          </div>
        </div>

        <Separator />

        {/* Budget lines */}
        <Collapsible open={openAdvanced} onOpenChange={setOpenAdvanced}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">
                Postes budgétaires ({budget_lines.length}) · Emprunts ({loans.length}) · Articles ({legal_articles.length})
              </div>
              <div className="text-xs text-muted-foreground">
                Cliquez pour éditer les lignes détaillées
              </div>
            </div>
            <CollapsibleTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid={`review-advanced-toggle-${doc.id}`}
              >
                {openAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                {openAdvanced ? "Replier" : "Déplier"}
              </Button>
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent className="mt-4 space-y-5">
            {/* Budget lines */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold">Postes budgétaires</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addBudgetLine}
                  data-testid={`add-budget-line-${doc.id}`}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter
                </Button>
              </div>
              {budget_lines.length === 0 ? (
                <p className="text-xs text-muted-foreground">Aucune ligne budgétaire extraite.</p>
              ) : (
                <div className="overflow-hidden rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left">Secteur</th>
                        <th className="px-3 py-2 text-right">Montant</th>
                        <th className="px-3 py-2 text-right">%</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {budget_lines.map((b, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-3 py-2">
                            <Input
                              className="h-8 text-sm"
                              value={asString(b.sector)}
                              onChange={(e) => updateBudgetLine(i, "sector", e.target.value)}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              className="h-8 text-sm text-right font-mono"
                              value={asString(b.amount)}
                              onChange={(e) => updateBudgetLine(i, "amount", e.target.value)}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              className="h-8 text-sm text-right font-mono"
                              value={asString(b.percentage)}
                              onChange={(e) => updateBudgetLine(i, "percentage", e.target.value)}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeBudgetLine(i)}
                              aria-label="Supprimer"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Loans */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-semibold">Emprunts / dette</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addLoan}
                  data-testid={`add-loan-${doc.id}`}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter
                </Button>
              </div>
              {loans.length === 0 ? (
                <p className="text-xs text-muted-foreground">Aucun emprunt extrait.</p>
              ) : (
                <div className="overflow-hidden rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left">Créancier</th>
                        <th className="px-3 py-2 text-right">Montant</th>
                        <th className="px-3 py-2 text-right">Taux %</th>
                        <th className="px-3 py-2 text-right">Maturité (ans)</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loans.map((l, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-3 py-2">
                            <Input
                              className="h-8 text-sm"
                              value={asString(l.creditor)}
                              onChange={(e) => updateLoan(i, "creditor", e.target.value)}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              className="h-8 text-sm text-right font-mono"
                              value={asString(l.amount)}
                              onChange={(e) => updateLoan(i, "amount", e.target.value)}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              className="h-8 text-sm text-right font-mono"
                              value={asString(l.rate)}
                              onChange={(e) => updateLoan(i, "rate", e.target.value)}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              className="h-8 text-sm text-right font-mono"
                              value={asString(l.maturity)}
                              onChange={(e) => updateLoan(i, "maturity", e.target.value)}
                            />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeLoan(i)}
                              aria-label="Supprimer"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Legal articles (read-only here) */}
            {legal_articles.length > 0 && (
              <div>
                <div className="mb-2 text-sm font-semibold">Articles juridiques</div>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {legal_articles.map((a, i) => (
                    <li key={i}>
                      <strong className="text-foreground">{a.article}</strong>
                      {" — "}
                      {a.content}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Action bar */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          <div className="mr-auto text-xs text-muted-foreground">
            Total lignes&nbsp;: <strong>{formatNumber(budget_lines.length)}</strong> · Total emprunts&nbsp;:{" "}
            <strong>{formatNumber(loans.length)}</strong>
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || saving}
            onClick={handleReset}
            data-testid={`review-reset-${doc.id}`}
          >
            <Undo2 className="mr-1 h-3.5 w-3.5" /> Annuler
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
            data-testid={`review-save-${doc.id}`}
          >
            <Save className="mr-1 h-3.5 w-3.5" />
            {saving ? "Enregistrement…" : "Enregistrer les corrections"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
